<template>
  <div>
    <!-- Page Header -->
    <CCard class="mb-4 border-0 shadow-sm">
      <CCardBody class="p-4">
        <CRow class="align-items-center">
          <CCol md="6">
            <h1 class="h4 mb-1 font-weight-bold">
              {{ obj.id ? `Bill ${obj.billNo || ""}`.trim() : "New bill" }}
              <CBadge
                v-if="obj.id"
                :color="badgeColor('bill', obj.stateDescription || 'Draft')"
                class="ml-2 align-middle"
              >
                {{ badgeLabel('bill', obj.stateDescription || 'Draft') }}
              </CBadge>
            </h1>
            <p class="text-muted mb-0">
              {{
                obj.id
                  ? (selectedVendor && selectedVendor.name) || "Vendor bill"
                  : "Record a bill from one of your vendors"
              }}
            </p>
            <p v-if="obj.id && obj.purchaseOrderId" class="small text-muted mb-0">
              From purchase order
              <router-link :to="`/tenants/PurchaseOrder/${obj.purchaseOrderId}`" class="text-monospace">{{
                obj.purchaseOrderNumber || "a purchase order"
              }}</router-link>
            </p>
          </CCol>
          <CCol md="6" class="d-flex justify-content-md-end mt-3 mt-md-0">
            <CDropdown
              placement="bottom-end"
              toggler-text="Actions"
              color="light"
              class="mr-2"
            >
              <CDropdownItem @click="addNew()">New bill</CDropdownItem>
              <CDropdownItem v-if="obj.id" @click="onDuplicate(obj)">Duplicate</CDropdownItem>
              <template v-if="computedStatuses.length">
                <CDropdownDivider />
                <CDropdownItem
                  v-for="status in computedStatuses"
                  :key="status.id"
                  @click="changeState(status)"
                >{{ statusActionLabel(status) }}</CDropdownItem>
              </template>
            </CDropdown>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <!-- Main Form -->
    <CRow>
      <CCol lg="12">
        <CCard class="mb-4 border-0 shadow-sm">
          <CCardHeader class="bg-transparent border-0 p-4">
            <h5 class="mb-0">Bill details</h5>
          </CCardHeader>
          <CCardBody class="p-4">
            <CAlert v-if="isApproved" color="info" class="mb-4">
              <CIcon name="cil-info" class="mr-2" />
              This bill is approved and posted to your accounts. To change lines, tax or
              dates, move it back to draft first. Payments can still be added.
            </CAlert>

            <!-- Vendor & Bill Info -->
            <CRow>
              <!-- Left Column: Vendor Info -->
              <CCol md="6">
                <div class="form-group" ref="vendorField">
                  <label for="bill-vendor">Vendor *</label>
                  <v-select
                    input-id="bill-vendor"
                    ref="vendorSelect"
                    style="width: 100%"
                    :class="{ 'is-invalid': errors.vendor }"
                    v-model="selectedVendor"
                    :label="'name'"
                    :options="vendorItemsWithAddNew"
                    placeholder="Select vendor"
                    @input="handleVendorSelect"
                  >
                    <span slot="no-options">
                      <CButton color="link" class="p-0" @click="addNewVendor">Add new vendor</CButton>
                    </span>
                  </v-select>
                  <div v-if="errors.vendor" class="invalid-feedback d-block">{{ errors.vendor }}</div>
                </div>

                <!-- Vendor Preview Card -->
                <div
                  v-if="selectedVendor && selectedVendor.id !== 'add_new'"
                  class="vendor-details p-3 bg-light rounded mb-3"
                >
                  <div class="d-flex align-items-center mb-2">
                    <div
                      class="c-avatar c-avatar-sm bg-primary text-white mr-2"
                    >
                      {{
                        selectedVendor.name
                          ? selectedVendor.name.charAt(0)
                          : "?"
                      }}
                    </div>
                    <div class="font-weight-bold">
                      {{ selectedVendor.name }}
                    </div>
                  </div>
                  <div
                    class="small text-muted mb-1"
                    v-if="selectedVendor.address"
                  >
                    <CIcon name="cil-location-pin" />
                    {{ selectedVendor.address }}
                  </div>
                  <div
                    class="small text-muted mb-1"
                    v-if="selectedVendor.contactEmail"
                  >
                    <CIcon name="cil-envelope-closed" />
                    {{ selectedVendor.contactEmail }}
                  </div>
                  <div
                    class="small text-muted"
                    v-if="selectedVendor.contactPhone"
                  >
                    <CIcon name="cil-phone" />
                    {{ selectedVendor.contactPhone }}
                  </div>
                  <CButton
                    color="link"
                    size="sm"
                    @click="viewVendorDetails"
                    class="p-0 mt-2"
                  >
                    View vendor
                  </CButton>
                </div>

                <CInput
                  label="Contact person"
                  v-model="obj.contactName"
                  placeholder="Contact person name"
                />
              </CCol>

              <!-- Right Column: Bill Info -->
              <CCol md="6">
                <CInput label="Bill number" v-model="obj.billNo" placeholder="Auto-generated if left blank" />
                <CInput label="Title" v-model="obj.title" placeholder="e.g., Office supplies purchase" />
                <CInput
                  label="Reference (PO no.)"
                  v-model="obj.reference"
                  placeholder="e.g., PO-12345"
                />
                <div v-if="isApproved" class="form-group">
                  <label>Date</label>
                  <div class="py-2">{{ formatDate(computeIssuedDate) }}</div>
                </div>
                <CInput
                  v-else
                  label="Date"
                  type="date"
                  :value="computeIssuedDate"
                  @change="setIssuedDate"
                />
                <CInput
                  label="Due date"
                  type="date"
                  :value="computeDueDate"
                  @change="setDueDate"
                />
              </CCol>
            </CRow>

            <!-- Notes -->
            <CRow>
              <CCol md="12">
                <CTextarea
                  label="Notes"
                  v-model="obj.note"
                  rows="3"
                  placeholder="Additional notes about this bill"
                />
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- Documents Section (Tab Interface) -->
    <CRow>
      <CCol lg="12">
        <CCard class="mb-4 border-0 shadow-sm">
          <CCardHeader class="bg-transparent border-0 p-4">
            <h5 class="mb-0">Documents</h5>
          </CCardHeader>
          <CCardBody class="p-4">
            <CTabs :active-tab="0" variant="tabs" add-tab-classes="mb-3">
              <!-- Tab 1: Invoice/Bill Document -->
              <CTab title="Invoice/Bill">
                <div v-if="obj.documentInvoiceId">
                  <!-- Display existing document -->
                  <div v-if="obj.documentInvoice && isPdf(obj.documentInvoice)">
                    <iframe
                      :src="invoiceImageUrl"
                      width="100%"
                      height="600px"
                    ></iframe>
                  </div>
                  <div v-else>
                    <CImg
                      :src="invoiceImageUrl"
                      width="100%"
                      responsive
                    />
                  </div>

                  <div class="mt-3">
                    <a
                      :href="invoiceImageUrl"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="text-primary mr-3"
                    >
                      <CIcon name="cil-external-link" /> Open in new tab
                    </a>
                    <CButton
                      color="danger"
                      size="sm"
                      @click="removeInvoiceDocumentConfirmation()"
                    >
                      <CIcon name="cil-trash" /> Remove
                    </CButton>
                  </div>

                  <!-- EXTRACT DATA BUTTON (rewrites lines, tax and date: drafts only) -->
                  <CAlert
                    v-if="!isApproved"
                    color="info"
                    class="mt-4 d-flex align-items-center justify-content-between"
                  >
                    <div>
                      <strong>Read data from the document</strong>
                      <p class="mb-0 small">
                        Fill in the bill details automatically from this file
                      </p>
                    </div>
                    <CButton
                      color="primary"
                      @click="onExtractData"
                      :disabled="extracting"
                    >
                      <CSpinner v-if="extracting" size="sm" class="mr-2" />
                      <CIcon v-else name="cil-data-transfer-down" class="mr-2" />
                      {{ extracting ? "Extracting…" : "Extract data" }}
                    </CButton>
                  </CAlert>

                  <CAlert
                    v-if="extractionSuccess"
                    color="success"
                    class="mt-3"
                    :show="5"
                  >
                    <CIcon name="cil-check-circle" class="mr-2" />
                    Data extracted. Check the details before you save.
                  </CAlert>
                </div>

                <!-- Upload widget if no document -->
                <div v-else>
                  <CAlert color="light" class="mb-3">
                    <CIcon name="cil-info" class="mr-2" />
                    Upload the vendor's invoice (PDF or image) to fill in the details automatically
                  </CAlert>
                  <WidgetsUploadImage
                    :billImageUrl="invoiceImageUrl"
                    @uploaded="uploaded_invoice"
                  />
                </div>
              </CTab>

              <!-- Tab 2: Payment Proof -->
              <CTab title="Payment proof">
                <div v-if="obj.documentPaymentId">
                  <div v-if="obj.documentPayment && isPdf(obj.documentPayment)">
                    <iframe
                      :src="paymentImageUrl"
                      width="100%"
                      height="600px"
                    ></iframe>
                  </div>
                  <div v-else>
                    <CImg
                      :src="paymentImageUrl"
                      width="100%"
                      responsive
                    />
                  </div>
                  <div class="mt-3">
                    <a
                      :href="paymentImageUrl"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="text-primary mr-3"
                    >
                      <CIcon name="cil-external-link" /> Open in new tab
                    </a>
                    <CButton
                      color="danger"
                      size="sm"
                      @click="removePaymentDocumentConfirmation()"
                    >
                      <CIcon name="cil-trash" /> Remove
                    </CButton>
                  </div>
                </div>
                <div v-else>
                  <WidgetsUploadImage
                    :billImageUrl="paymentImageUrl"
                    @uploaded="uploaded_payment"
                  />
                </div>
              </CTab>

              <!-- Tab 3: Receipt -->
              <CTab title="Receipt">
                <div v-if="obj.documentReceiptId">
                  <div v-if="obj.documentReceipt && isPdf(obj.documentReceipt)">
                    <iframe
                      :src="receiptImageUrl"
                      width="100%"
                      height="600px"
                    ></iframe>
                  </div>
                  <div v-else>
                    <CImg
                      :src="receiptImageUrl"
                      width="100%"
                      responsive
                    />
                  </div>
                  <div class="mt-3">
                    <a
                      :href="receiptImageUrl"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="text-primary mr-3"
                    >
                      <CIcon name="cil-external-link" /> Open in new tab
                    </a>
                    <CButton
                      color="danger"
                      size="sm"
                      @click="removeReceiptDocumentConfirmation()"
                    >
                      <CIcon name="cil-trash" /> Remove
                    </CButton>
                  </div>
                </div>
                <div v-else>
                  <WidgetsUploadImage
                    :billImageUrl="receiptImageUrl"
                    @uploaded="uploaded_receipt"
                  />
                </div>
              </CTab>
            </CTabs>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- Line Items -->
    <CRow>
      <CCol lg="12">
        <CCard class="mb-4 border-0 shadow-sm">
          <CCardHeader class="bg-transparent border-0 p-4">
            <h5 class="mb-0">Line items *</h5>
          </CCardHeader>
          <CCardBody class="p-4">
            <div v-if="errors.items" ref="itemsError" tabindex="-1" class="invalid-feedback d-block mb-3" role="alert">
              {{ errors.items }}
            </div>
            <BillItemsComponent
              :items="obj.items"
              :productItems="productItems"
              :grandTotal="computedSubtotal"
              :grandTaxOnly="computedTaxAmount"
              :grandTotalWithTax="computedGrandTotal"
              :selectedSalesTax="obj.salesTax"
              :salesTaxItems="salesTaxItems"
              :showTax="true"
              :useDescriptionModal="false"
              :readonly="isApproved"
              @add-item="addItem"
              @remove-item="removeItem"
              @move-item="moveItem"
              @product-selected="handleProductSelect"
              @tax-selected="handleTaxSelect"
            />
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- Auto-Calculation Summary -->
    <CRow>
      <CCol lg="12">
        <CCard class="mb-4 border-0 shadow-sm">
          <CCardBody class="p-4">
            <CRow>
              <CCol md="6" class="offset-md-6">
                <div class="d-flex justify-content-between mb-2">
                  <div>Subtotal</div>
                  <div class="font-weight-bold text-right">RM {{ formatCurrency(computedSubtotal) }}</div>
                </div>
                <div v-if="obj.salesTax" class="d-flex justify-content-between mb-2">
                  <div>Tax ({{ obj.salesTax.name }}, {{ obj.salesTax.rateInPercentage }}%)</div>
                  <div class="font-weight-bold text-right">RM {{ formatCurrency(computedTaxAmount) }}</div>
                </div>
                <div class="d-flex justify-content-between border-top pt-2 mt-2">
                  <div class="h5 mb-0">Grand total</div>
                  <div class="h5 mb-0 text-primary text-right">RM {{ formatCurrency(computedGrandTotal) }}</div>
                </div>
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- Payment Tracking Section -->
    <CRow>
      <CCol lg="12">
        <CCard class="mb-4 border-0 shadow-sm">
          <CCardHeader class="bg-transparent border-0 p-4">
            <h5 class="mb-0">
              <CIcon name="cil-dollar" class="mr-2" />
              Payments
            </h5>
          </CCardHeader>
          <CCardBody class="p-4">
            <BillPaymentsComponent
              :payments="obj.payments"
              :billTotal="computedGrandTotal"
              :isReadOnly="false"
              @add-payment="addPayment"
              @remove-payment="removePayment"
              @payment-change="onPaymentChange"
              @payment-proof-uploaded="onPaymentProofUploaded"
              @payment-proof-removed="onPaymentProofRemoved"
              @show-toast="toast"
            />
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- Save Button -->
    <CRow>
      <CCol lg="12">
        <CCard class="mb-4 border-0 shadow-sm">
          <CCardFooter class="p-4 d-flex justify-content-end">
            <CButton color="light" class="mr-2" :disabled="isLoading" :to="'/tenants/BillList'">
              Cancel
            </CButton>
            <CButton color="primary" @click="submit" :disabled="isLoading">
              <CSpinner v-if="isLoading" size="sm" class="mr-2" />
              {{ obj.id ? "Save" : "Create bill" }}
            </CButton>
          </CCardFooter>
        </CCard>
      </CCol>
    </CRow>

    <!-- Status change confirmation (approving posts to the ledger, Draft reverses it) -->
    <CModal
      :title="statusConfirmTitle"
      :show.sync="statusConfirmModal"
      :close-on-backdrop="!changingState"
      @update:show="onStatusConfirmModalClose"
    >
      <p class="mb-0">{{ statusConfirmMessage }}</p>
      <template #footer>
        <CButton color="light" :disabled="changingState" @click="cancelStatusChange">
          Cancel
        </CButton>
        <CButton color="primary" :disabled="changingState" @click="confirmStatusChange">
          <CSpinner v-if="changingState" size="sm" class="mr-1" />
          {{ statusConfirmButton }}
        </CButton>
      </template>
    </CModal>

    <!-- Remove document confirmations -->
    <CModal
      title="Remove the invoice document?"
      :show.sync="removeInvoiceDocumentWarningModal"
    >
      The uploaded vendor invoice will be removed from this bill. The bill itself stays as it is.
      <template #footer>
        <CButton color="light" @click="removeInvoiceDocumentWarningModal = false">Keep it</CButton>
        <CButton color="danger" @click="removeInvoiceDocumentWarningModal = false; onRemoveInvoiceDocumentConfirmation(false, null, true)">
          Remove document
        </CButton>
      </template>
    </CModal>

    <CModal
      title="Remove the payment proof?"
      :show.sync="removePaymentDocumentWarningModal"
    >
      The uploaded payment proof will be removed from this bill. Recorded payments stay as they are.
      <template #footer>
        <CButton color="light" @click="removePaymentDocumentWarningModal = false">Keep it</CButton>
        <CButton color="danger" @click="removePaymentDocumentWarningModal = false; onRemovePaymentDocumentConfirmation(false, null, true)">
          Remove document
        </CButton>
      </template>
    </CModal>

    <CModal
      title="Remove the receipt?"
      :show.sync="removeReceiptDocumentWarningModal"
    >
      The uploaded receipt will be removed from this bill. The bill itself stays as it is.
      <template #footer>
        <CButton color="light" @click="removeReceiptDocumentWarningModal = false">Keep it</CButton>
        <CButton color="danger" @click="removeReceiptDocumentWarningModal = false; onRemoveReceiptDocumentConfirmation(false, null, true)">
          Remove document
        </CButton>
      </template>
    </CModal>

    <!-- Add Vendor Modal (shared) -->
    <VendorFormModal
      :show="addVendorFormPopup"
      :vendor="itemAddNewVendor"
      @close="addVendorFormPopup = false"
      @save="onVendorSave"
    />
  </div>
</template>

<script>
import BillApi from "../../lib/billApi";
import BillItemsComponent from "../components/BillItemsComponent.vue";
import BillPaymentsComponent from "../components/BillPaymentsComponent.vue";
import VendorApi from "@/lib/vendorApi";
import ProductApi from "@/lib/productApi";
import SalesTaxApi from "@/lib/salesTaxApi";
import WidgetsUploadImage from "../widgets/WidgetsUploadImage.vue";
import VendorFormModal from "../components/VendorFormModal.vue";
import { mytToday, parseDateInput, formatDate, asDateString } from "@/utils/datetime";
import { formatMyr } from "@/utils/money";
import { badgeColor, badgeLabel } from "@/utils/statusBadges";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";

export default {
  name: "Bill",
  components: {
    BillItemsComponent,
    BillPaymentsComponent,
    WidgetsUploadImage,
    VendorFormModal,
    vSelect,
  },
  data() {
    return {
      api: new BillApi(),
      vendorApi: new VendorApi(),
      productApi: new ProductApi(),
      salesTaxApi: new SalesTaxApi(),

      obj: {
        items: [],
        payments: [],
        salesTax: null,
        state: 0, // Draft
        isPaid: false,
      },

      // Vendors
      vendorItems: [],
      selectedVendor: null,

      // Products
      productItems: [],

      // Sales Taxes
      salesTaxItems: [],

      // Extraction state
      extracting: false,
      extractionSuccess: false,

      // Other state
      statuses: [],
      isLoading: false,
      errors: { vendor: "", items: "" },

      // Status change confirmation
      statusConfirmModal: false,
      pendingStatusChange: null,
      changingState: false,

      // Dates
      issuedDate: new Date(),
      dueDate: null,
      paymentDate: new Date(),

      // Modals
      removeInvoiceDocumentWarningModal: false,
      removePaymentDocumentWarningModal: false,
      removeReceiptDocumentWarningModal: false,
      addVendorFormPopup: false,
      itemAddNewVendor: {},
    };
  },

  computed: {
    // A saved, approved bill is posted to the ledger: the API refuses changes to its lines,
    // tax, date, business or category account until it is moved back to Draft.
    isApproved() {
      return !!this.obj.id && this.obj.stateDescription === "Approve";
    },

    computedStatuses() {
      if (!this.obj.id) return [];
      return this.statuses.filter((status) => status.name !== this.obj.stateDescription);
    },

    isPendingApprove() {
      return !!this.pendingStatusChange && this.pendingStatusChange.name === "Approve";
    },

    isPendingDraft() {
      return !!this.pendingStatusChange && this.pendingStatusChange.name === "Draft";
    },

    statusConfirmTitle() {
      const ref = this.obj.billNo ? ` ${this.obj.billNo}` : "";
      if (this.isPendingApprove) return `Approve bill${ref}?`;
      if (this.isPendingDraft) return `Move bill${ref} back to draft?`;
      const name = this.pendingStatusChange ? badgeLabel("bill", this.pendingStatusChange.name).toLowerCase() : "";
      return `Change bill${ref} to ${name}?`;
    },

    statusConfirmMessage() {
      const ref = this.obj.billNo ? ` ${this.obj.billNo}` : "";
      const amount = `RM ${this.formatCurrency(this.computedGrandTotal)}`;
      if (this.isPendingApprove)
        return `Bill${ref} for ${amount} will be approved and posted to your accounts.`;
      if (this.isPendingDraft)
        return `The posting of bill${ref} for ${amount} will be reversed in your accounts. You can then change it and approve it again.`;
      const name = this.pendingStatusChange ? badgeLabel("bill", this.pendingStatusChange.name).toLowerCase() : "";
      return `Bill${ref} for ${amount} will be changed to ${name}.`;
    },

    statusConfirmButton() {
      if (this.isPendingApprove) return "Approve bill";
      if (this.isPendingDraft) return "Move to draft";
      return "Change status";
    },

    vendorItemsWithAddNew() {
      return [
        ...this.vendorItems,
        { id: "add_new", name: "+ Add new vendor" },
      ];
    },

    computedSubtotal() {
      return this.obj.items.reduce((sum, item) => {
        return sum + (parseFloat(item.amount) || 0);
      }, 0);
    },

    computedTaxAmount() {
      if (!this.obj.salesTax) return 0;
      return (this.computedSubtotal * this.obj.salesTax.rateInPercentage) / 100;
    },

    computedGrandTotal() {
      return this.computedSubtotal + this.computedTaxAmount;
    },

    computeIssuedDate() {
      return asDateString(this.obj.date);
    },

    computeDueDate() {
      return asDateString(this.obj.dueDate);
    },

    computePaymentDate() {
      return asDateString(this.obj.paymentDate);
    },

    invoiceImageUrl() {
      return this.obj.documentInvoiceId
        ? apiUrl + "documents/file/" + this.obj.documentInvoiceId
        : "";
    },

    paymentImageUrl() {
      return this.obj.documentPaymentId
        ? apiUrl + "documents/file/" + this.obj.documentPaymentId
        : "";
    },

    receiptImageUrl() {
      return this.obj.documentReceiptId
        ? apiUrl + "documents/file/" + this.obj.documentReceiptId
        : "";
    },
  },

  mounted() {
    this.setIssuedDate(mytToday());
    this.setPaymentDate(mytToday());

    this.loadVendors();
    this.loadProducts();
    this.loadSalesTaxes();
    this.loadStatuses();

    // Check if route has an ID and it's not "new"
    if (this.$route.params.id && this.$route.params.id !== 'new') {
      this.loadBill(this.$route.params.id);
    } else {
      this.obj = this.getEmptyBill();
    }
  },

  methods: {
    // EXTRACTION METHOD
    async onExtractData() {
      this.extracting = true;
      this.extractionSuccess = false;

      try {
        const documentId = this.obj.documentInvoiceId;
        if (!documentId) {
          this.$toast.warning("Upload the vendor's invoice first");
          return;
        }

        const extracted = await this.api.extractInvoiceData(documentId);

        // Populate basic fields
        this.obj.billNo = extracted.billNumber || this.obj.billNo;
        this.obj.date = extracted.date || this.obj.date;
        this.obj.dueDate = extracted.dueDate;
        this.obj.reference = extracted.reference;
        this.obj.title = extracted.title;
        this.obj.contactName = extracted.contactName;
        this.obj.note = extracted.notes;

        // Try to match vendor by name
        if (extracted.vendorName && this.vendorItems.length > 0) {
          const matchingVendor = this.vendorItems.find(
            v => v.name.toLowerCase().includes(extracted.vendorName.toLowerCase())
          );
          if (matchingVendor) {
            this.selectedVendor = matchingVendor;
            this.obj.vendorId = matchingVendor.id;
          }
        }

        // Populate line items
        this.obj.items = extracted.items.map((item, index) => ({
          id: null,
          productId: null,
          product: null,
          name: null,
          description: item.description,
          quantity: item.quantity,
          unit: item.unit || 'pcs',
          price: item.unitPrice,
          amount: item.amount,
          position: index
        }));

        // Find and set tax if rate matches
        if (extracted.taxRate && this.salesTaxItems.length > 0) {
          const matchingTax = this.salesTaxItems.find(
            tax => tax.rateInPercentage === extracted.taxRate
          );
          if (matchingTax) {
            this.obj.salesTax = matchingTax;
            this.obj.salesTaxId = matchingTax.id;
          }
        }

        this.extractionSuccess = true;
        this.$toast.success("Data extracted. Check the details before you save.");

      } catch (error) {
        console.error('Extraction error:', error);
        this.$toast.error("Couldn't read data from this document. Enter the details by hand.");
      } finally {
        this.extracting = false;
      }
    },

    // ITEM MANAGEMENT
    addItem() {
      this.obj.items.push({
        id: null,
        productId: null,
        product: null,
        name: '',
        description: '',
        quantity: 1,
        unit: 'pcs',
        price: 0,
        amount: 0,
        position: this.obj.items.length
      });
    },

    removeItem(item) {
      const index = this.obj.items.indexOf(item);
      if (index > -1) {
        this.obj.items.splice(index, 1);
        this.updateItemPositions();
      }
    },

    moveItem(index, direction) {
      if (direction === 'up' && index > 0) {
        const temp = this.obj.items[index];
        this.$set(this.obj.items, index, this.obj.items[index - 1]);
        this.$set(this.obj.items, index - 1, temp);
      } else if (direction === 'down' && index < this.obj.items.length - 1) {
        const temp = this.obj.items[index];
        this.$set(this.obj.items, index, this.obj.items[index + 1]);
        this.$set(this.obj.items, index + 1, temp);
      }
      this.updateItemPositions();
    },

    handleProductSelect(product, item) {
      if (product) {
        item.productId = product.id;
        item.product = product;
        item.name = product.name;
        item.description = product.description || '';
        item.price = product.price || 0;
        item.unit = product.unit || 'pcs';
        this.calculateItemAmount(item);
      }
    },

    handleTaxSelect(tax) {
      if (tax) {
        this.obj.salesTax = tax;
        this.obj.salesTaxId = tax.id;
      } else {
        this.obj.salesTax = null;
        this.obj.salesTaxId = null;
      }
    },

    calculateItemAmount(item) {
      item.amount = (parseFloat(item.quantity) || 0) * (parseFloat(item.price) || 0);
    },

    updateItemPositions() {
      this.obj.items.forEach((item, index) => {
        item.position = index;
      });
    },

    // PAYMENT MANAGEMENT
    async addPayment(payment) {
      if (!this.obj.id) {
        // Bill not saved yet, just add to local array
        this.obj.payments.push(payment);
        return;
      }

      try {
        const savedPayment = await this.api.addPayment(this.obj.id, payment);
        this.obj.payments.push(savedPayment.result);
        this.$toast.success("Payment added");
      } catch (error) {
        console.error('[Bill.vue] Failed to add payment - Full error details:', {
          error,
          message: error.message,
          response: error.response,
          responseData: error.response?.data,
          status: error.response?.status,
          statusText: error.response?.statusText,
          config: error.config
        });

        // Extract backend error message for better user experience
        let errorMessage = "";

        // Try multiple possible error paths (API wrapper formats vary)
        if (error.data?.responseException?.exceptionMessage?.message) {
          // AutoWrapper error format (wrapped by api.js)
          errorMessage = error.data.responseException.exceptionMessage.message;
        } else if (error.response?.data?.responseException?.exceptionMessage?.message) {
          // Direct axios error format
          errorMessage = error.response.data.responseException.exceptionMessage.message;
        } else if (error.data?.message) {
          errorMessage = error.data.message;
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        }

        this.$toast.error("Couldn't add the payment. " + (errorMessage || "Please try again."));

        // If the error is about exceeding bill total, refresh the bill to get latest data
        if (errorMessage.includes('exceed bill total') || errorMessage.includes('amount due')) {
          if (this.obj.id) {
            this.loadBill(this.obj.id);
          }
        }
      }
    },

    async removePayment(index) {
      const payment = this.obj.payments[index];

      if (!payment.id || !this.obj.id) {
        // Payment not saved yet or bill not saved, just remove from local array
        this.obj.payments.splice(index, 1);
        return;
      }

      try {
        await this.api.deletePayment(this.obj.id, payment.id);
        this.obj.payments.splice(index, 1);
        this.$toast.success("Payment removed");
      } catch (error) {
        console.error('Failed to remove payment:', error);
        this.$toast.error(helper.getErrorMessage(error && error.data, "Couldn't remove the payment. Please try again."));
      }
    },

    async onPaymentChange(payment) {
      // Save the specific payment that changed
      if (!this.obj.id || !payment.id) {
        this.$forceUpdate();
        return;
      }

      try {
        await this.api.updatePayment(this.obj.id, payment.id, payment);
      } catch (error) {
        console.error('Failed to update payment:', error);
        this.$toast.error(helper.getErrorMessage(error && error.data, "Couldn't save the payment. Please try again."));
      }
    },

    onPaymentProofUploaded(index, document) {
      const payment = this.obj.payments[index];
      if (payment) {
        payment.documentPaymentProofId = document.id;
        payment.documentPaymentProof = {
          id: document.id,
          contentType: document.contentType,
          fileName: document.fileName
        };
        // Trigger auto-save
        this.onPaymentChange(payment);
      }
    },

    onPaymentProofRemoved(index) {
      const payment = this.obj.payments[index];
      if (payment) {
        payment.documentPaymentProofId = null;
        payment.documentPaymentProof = null;
        // Trigger auto-save
        this.onPaymentChange(payment);
      }
    },

    // VENDOR MANAGEMENT
    handleVendorSelect(selected) {
      if (selected && selected.id === "add_new") {
        this.addNewVendor();
        this.selectedVendor = null;
      } else if (selected) {
        this.obj.vendorId = selected.id;
        this.obj.vendor = selected;
        this.errors.vendor = "";
      }
    },

    addNewVendor() {
      this.itemAddNewVendor = {};
      this.addVendorFormPopup = true;
    },

    viewVendorDetails() {
      if (this.selectedVendor && this.selectedVendor.id) {
        this.$router.push({
          path: `/tenants/Vendor/${this.selectedVendor.id}`,
        });
      }
    },

    onVendorSave(vendor) {
      this.vendorApi
        .create(vendor)
        .then((response) => {
          const addedVendor = response.result;
          this.loadVendors();
          this.selectedVendor = addedVendor;
          this.obj.vendorId = addedVendor.id;
          this.errors.vendor = "";
          this.addVendorFormPopup = false;
          this.itemAddNewVendor = {};
          this.$toast.success("Vendor added");
        })
        .catch((err) => {
          this.$toast.error("Couldn't add the vendor. " + helper.getErrorMessage(err && err.data, "Please try again."));
        });
    },

    // DATE SETTERS
    setIssuedDate(value) {
      this.issuedDate = value;
      this.obj.date = parseDateInput(value) || null;
    },

    setDueDate(value) {
      this.dueDate = value;
      this.obj.dueDate = parseDateInput(value) || null;
    },

    setPaymentDate(value) {
      this.paymentDate = value;
      this.obj.paymentDate = parseDateInput(value) || null;
    },

    // DOCUMENT MANAGEMENT
    uploaded_invoice(data) {
      const uploadedFiles = data.uploadedFiles;
      if (uploadedFiles && uploadedFiles.length > 0) {
        const file = uploadedFiles[0];
        this.obj.documentInvoiceId = file.id;
        // Store minimal document info for display (contentType needed for PDF detection)
        this.obj.documentInvoice = {
          id: file.id,
          contentType: file.contentType,
          fileName: file.fileName
        };
        this.$toast.success("Invoice document uploaded");
      } else {
        this.$toast.error("Couldn't upload the invoice document. Please try again.");
      }
    },

    uploaded_payment(data) {
      const uploadedFiles = data.uploadedFiles;
      if (uploadedFiles && uploadedFiles.length > 0) {
        const file = uploadedFiles[0];
        this.obj.documentPaymentId = file.id;
        // Store minimal document info for display (contentType needed for PDF detection)
        this.obj.documentPayment = {
          id: file.id,
          contentType: file.contentType,
          fileName: file.fileName
        };
        this.$toast.success("Payment proof uploaded");
      } else {
        this.$toast.error("Couldn't upload the payment proof. Please try again.");
      }
    },

    uploaded_receipt(data) {
      const uploadedFiles = data.uploadedFiles;
      if (uploadedFiles && uploadedFiles.length > 0) {
        const file = uploadedFiles[0];
        this.obj.documentReceiptId = file.id;
        // Store minimal document info for display (contentType needed for PDF detection)
        this.obj.documentReceipt = {
          id: file.id,
          contentType: file.contentType,
          fileName: file.fileName
        };
        this.$toast.success("Receipt uploaded");
      } else {
        this.$toast.error("Couldn't upload the receipt. Please try again.");
      }
    },

    removeInvoiceDocumentConfirmation() {
      this.removeInvoiceDocumentWarningModal = true;
    },

    onRemoveInvoiceDocumentConfirmation(status, evt, accept) {
      if (accept) {
        this.api
          .removeInvoiceDocument(this.obj.id, this.obj.documentInvoiceId)
          .then(() => {
            this.loadBill(this.obj.id);
            this.$toast.success("Document removed");
          })
          .catch(({ data }) => {
            this.$toast.error("Couldn't remove the document. " + helper.getErrorMessage(data, "Please try again."));
          });
      }
    },

    removePaymentDocumentConfirmation() {
      this.removePaymentDocumentWarningModal = true;
    },

    onRemovePaymentDocumentConfirmation(status, evt, accept) {
      if (accept) {
        this.api
          .removePaymentDocument(this.obj.id, this.obj.documentPaymentId)
          .then(() => {
            this.loadBill(this.obj.id);
            this.$toast.success("Document removed");
          })
          .catch(({ data }) => {
            this.$toast.error("Couldn't remove the document. " + helper.getErrorMessage(data, "Please try again."));
          });
      }
    },

    removeReceiptDocumentConfirmation() {
      this.removeReceiptDocumentWarningModal = true;
    },

    onRemoveReceiptDocumentConfirmation(status, evt, accept) {
      if (accept) {
        this.api
          .removeReceiptDocument(this.obj.id, this.obj.documentReceiptId)
          .then(() => {
            this.loadBill(this.obj.id);
            this.$toast.success("Document removed");
          })
          .catch(({ data }) => {
            this.$toast.error("Couldn't remove the document. " + helper.getErrorMessage(data, "Please try again."));
          });
      }
    },

    isPdf(document) {
      try {
        return document.contentType === "application/pdf";
      } catch (error) {
        return false;
      }
    },

    getDocumentUrl(document) {
      try {
        return apiUrl + "documents/file/" + document.id;
      } catch (error) {
        return "";
      }
    },

    // STATUS MANAGEMENT
    changeState(status) {
      if (!this.obj.id) {
        this.$toast.warning("Save the bill before changing its status");
        return;
      }
      this.pendingStatusChange = status;
      this.statusConfirmModal = true;
    },

    cancelStatusChange() {
      this.statusConfirmModal = false;
      this.pendingStatusChange = null;
    },

    onStatusConfirmModalClose(visible) {
      if (!visible && !this.changingState) this.pendingStatusChange = null;
    },

    confirmStatusChange() {
      if (this.changingState || !this.pendingStatusChange || !this.obj.id) return;
      const previousState = this.obj.state;
      this.obj.state = this.pendingStatusChange.id;
      this.changingState = true;
      this.api
        .updateState(this.obj)
        .then(() => {
          this.loadBill(this.obj.id);
          this.$toast.success(this.isPendingApprove ? "Bill approved" : this.isPendingDraft ? "Bill moved to draft" : "Status updated");
        })
        .catch((error) => {
          // Nothing changed on the server (e.g. the bill has payments, or its SST period
          // is closed): keep the old status and show the API's sentence.
          this.obj.state = previousState;
          console.error("[Bill] status change failed:", error);
          this.$toast.error("Couldn't change the status. " + helper.getErrorMessage(error && error.data, "Please try again."));
        })
        .finally(() => {
          this.statusConfirmModal = false;
          this.changingState = false;
          this.pendingStatusChange = null;
        });
    },

    badgeColor,
    badgeLabel,

    // Menu wording for a status transition ("Approve", "Move to draft")
    statusActionLabel(status) {
      if (status.name === "Approve") return "Approve";
      if (status.name === "Draft") return "Move to draft";
      return badgeLabel("bill", status.name);
    },

    // DATA LOADING
    async loadVendors() {
      try {
        const response = await this.vendorApi.getListByCurrentBusiness();
        this.vendorItems = response.result || [];
      } catch (error) {
        console.error('Failed to load vendors:', error);
      }
    },

    async loadProducts() {
      try {
        const response = await this.productApi.getListByCurrentBusiness();
        this.productItems = response.result || [];
      } catch (error) {
        console.error('Failed to load products:', error);
      }
    },

    async loadSalesTaxes() {
      try {
        const response = await this.salesTaxApi.getListByCurrentBusiness();
        this.salesTaxItems = response.result || [];
      } catch (error) {
        console.error('Failed to load sales taxes:', error);
      }
    },

    async loadStatuses() {
      try {
        const response = await this.api.getStatusTypes();
        this.statuses = response.result || [];
      } catch (error) {
        console.error('Failed to load statuses:', error);
      }
    },

    async loadBill(id) {
      try {
        const response = await this.api.get(id);
        this.obj = response.result;

        // Set dates
        if (this.obj.date) {
          this.issuedDate = this.obj.date;
        }
        if (this.obj.dueDate) {
          this.dueDate = this.obj.dueDate;
        }
        if (this.obj.paymentDate) {
          this.paymentDate = this.obj.paymentDate;
        }

        // Set selected vendor
        if (this.obj.vendor) {
          this.selectedVendor = this.obj.vendor;
        }

        // Ensure items array exists
        if (!this.obj.items) {
          this.obj.items = [];
        }

        // Ensure payments array exists
        if (!this.obj.payments) {
          this.obj.payments = [];
        }

        // Ensure salesTax is set if salesTaxId exists
        if (this.obj.salesTaxId && !this.obj.salesTax) {
          const tax = this.salesTaxItems.find(t => t.id === this.obj.salesTaxId);
          if (tax) {
            this.obj.salesTax = tax;
          }
        }

        // Explicitly extract document IDs from navigation properties if foreign keys are missing
        // This ensures documents display correctly after page refresh
        if (this.obj.documentInvoice && this.obj.documentInvoice.id && !this.obj.documentInvoiceId) {
          this.obj.documentInvoiceId = this.obj.documentInvoice.id;
        }
        if (this.obj.documentPayment && this.obj.documentPayment.id && !this.obj.documentPaymentId) {
          this.obj.documentPaymentId = this.obj.documentPayment.id;
        }
        if (this.obj.documentReceipt && this.obj.documentReceipt.id && !this.obj.documentReceiptId) {
          this.obj.documentReceiptId = this.obj.documentReceipt.id;
        }


      } catch (error) {
        console.error('Failed to load bill:', error);
        this.$toast.error("Couldn't load the bill. Check your connection and try again.");
      }
    },

    // SAVE
    async submit() {
      // Validation (inline, then focus the first problem)
      this.errors = { vendor: "", items: "" };
      if (!this.obj.vendorId) {
        this.errors.vendor = "Choose the vendor who sent this bill.";
      }
      if (!this.obj.items || this.obj.items.length === 0) {
        this.errors.items = "Add at least one line item.";
      } else if (this.obj.items.some((item) => !item.productId && !item.name)) {
        this.errors.items = "Every line needs a product or a name.";
      }
      if (this.errors.vendor || this.errors.items) {
        this.focusFirstError();
        return;
      }

      // Update grand total before saving
      this.obj.totalAmount = this.computedGrandTotal;

      // Ensure all items have calculated amounts
      this.obj.items.forEach(item => {
        if (!item.amount || item.amount === 0) {
          this.calculateItemAmount(item);
        }
      });

      this.isLoading = true;


      try {
        if (this.obj.id) {
          // Clean items array - remove navigation properties from each item
          // Note: Backend regenerates all item IDs, so we don't send id
          const cleanedItems = this.obj.items.map(item => ({
            productId: item.productId,
            name: item.name,
            description: item.description,
            quantity: item.quantity,
            unit: item.unit,
            price: item.price,
            amount: item.amount,
            position: item.position,
            // Keep the purchase order links, or the line loses its expense account and stops counting as billed
            purchaseProductId: item.purchaseProductId,
            purchaseOrderProductId: item.purchaseOrderProductId,
            // Exclude: id (backend regenerates), billId, product, bill navigation properties
          }));

          // Create a clean copy without navigation properties for update
          // Navigation properties cause EF tracking conflicts
          const billData = {
            ...this.obj,
            items: cleanedItems,  // Use cleaned items
            // Exclude navigation properties that shouldn't be sent
            vendor: undefined,
            salesTax: undefined,
            payments: undefined,  // Payments are managed via separate endpoints
            profile: undefined,
            business: undefined,
            tenant: undefined,
            chartAccount: undefined,
            documentInvoice: undefined,
            documentPayment: undefined,
            documentReceipt: undefined,
            drawCompany: undefined,
            drawBillItem: undefined,
            drawBillNo: undefined,
            drawDate: undefined,
            drawTotalAmount: undefined,
            // Keep foreign key IDs
            vendorId: this.obj.vendorId,
            salesTaxId: this.obj.salesTaxId,
            // Document foreign keys
            documentInvoiceId: this.obj.documentInvoiceId || undefined,
            documentPaymentId: this.obj.documentPaymentId || undefined,
            documentReceiptId: this.obj.documentReceiptId || undefined,
          };


          await this.api.update(billData);
          this.$toast.success("Bill saved");
        } else {
          // Clean items array - remove navigation properties from each item
          // Don't send id for new items (backend will generate)
          const cleanedItems = this.obj.items.map(item => ({
            productId: item.productId,
            name: item.name,
            description: item.description,
            quantity: item.quantity,
            unit: item.unit,
            price: item.price,
            amount: item.amount,
            position: item.position,
            // Keep the purchase order links, or the line loses its expense account and stops counting as billed
            purchaseProductId: item.purchaseProductId,
            purchaseOrderProductId: item.purchaseOrderProductId,
            // Exclude: id (null causes GUID conversion error), billId, product, bill navigation properties
          }));

          // Create a clean copy without navigation properties for create
          // Navigation properties cause backend validation errors
          const billData = {
            ...this.obj,
            id: undefined,  // Don't send id for CREATE (null causes GUID conversion error)
            items: cleanedItems,  // Use cleaned items
            // Exclude null DateTime fields (backend can't convert null to DateTime)
            dueDate: this.obj.dueDate || undefined,
            paymentDate: this.obj.paymentDate || undefined,
            // Exclude navigation properties that shouldn't be sent
            vendor: undefined,
            salesTax: undefined,
            payments: undefined,  // Payments are managed via separate endpoints
            profile: undefined,
            business: undefined,
            tenant: undefined,
            chartAccount: undefined,
            documentInvoice: undefined,
            documentPayment: undefined,
            documentReceipt: undefined,
            drawCompany: undefined,
            drawBillItem: undefined,
            drawBillNo: undefined,
            drawDate: undefined,
            drawTotalAmount: undefined,
            // Keep foreign key IDs
            vendorId: this.obj.vendorId,
            salesTaxId: this.obj.salesTaxId,
            // Document foreign keys
            documentInvoiceId: this.obj.documentInvoiceId || undefined,
            documentPaymentId: this.obj.documentPaymentId || undefined,
            documentReceiptId: this.obj.documentReceiptId || undefined,
          };


          const response = await this.api.create(billData);
          this.$toast.success("Bill created");
          this.$router.push({ path: `/tenants/Bill/${response.result.id}` });
        }
      } catch (error) {
        // e.g. an approved bill whose posted lines, tax or date changed: the API explains
        // what to do (move it back to Draft first).
        console.error('Save error:', error);
        this.$toast.error(helper.getErrorMessage(error && error.data, "Couldn't save the bill. Please try again."));
      } finally {
        this.isLoading = false;
      }
    },

    // UTILITY
    addNew() {
      this.$router.push({ path: "/tenants/Bill/new" });
      this.obj = this.getEmptyBill();
      this.selectedVendor = null;
    },

    onDuplicate(bill) {
      const duplicate = { ...bill };
      duplicate.id = null;
      duplicate.billNo = '';
      duplicate.date = mytToday();
      duplicate.documentInvoiceId = null;
      duplicate.documentPaymentId = null;
      duplicate.documentReceiptId = null;
      duplicate.state = 0; // Draft
      duplicate.stateDescription = "Draft";
      duplicate.isPaid = false;
      // A copy is not billed from the purchase order
      duplicate.purchaseOrderId = null;
      duplicate.purchaseOrderNumber = null;

      // Duplicate items (keep the expense item, drop the purchase order line link)
      duplicate.items = bill.items.map(item => ({
        ...item,
        id: null,
        purchaseOrderProductId: null
      }));

      this.obj = duplicate;
      this.$toast.info("Bill duplicated. Make your changes, then save");
    },

    formatDate,

    // Number part only ("1,234.50"); templates add the "RM " prefix.
    formatCurrency(amount) {
      return formatMyr(amount);
    },

    focusFirstError() {
      this.$nextTick(() => {
        if (this.errors.vendor && this.$refs.vendorField) {
          this.$refs.vendorField.scrollIntoView({ behavior: "smooth", block: "center" });
          const input = this.$refs.vendorField.querySelector("input");
          if (input) input.focus();
        } else if (this.errors.items && this.$refs.itemsError) {
          this.$refs.itemsError.scrollIntoView({ behavior: "smooth", block: "center" });
          this.$refs.itemsError.focus();
        }
      });
    },

    // Adapter for child components that emit (header, message, color)
    toast(header, message, color) {
      const kind = { success: "success", danger: "error", warning: "warning", info: "info" }[color] || "info";
      this.$toast[kind](message);
    },

    getEmptyBill() {
      return {
        id: null,
        vendorId: null,
        vendor: null,
        billNo: '',
        title: '',
        reference: '',
        date: mytToday(),
        dueDate: null,
        contactName: '',
        note: '',
        items: [],
        payments: [],
        salesTaxId: null,
        salesTax: null,
        totalAmount: 0,
        isPaid: false,
        paymentDate: null,
        paymentMethod: '',
        paymentReference: '',
        paymentNotes: '',
        state: 0, // Draft
        stateDescription: 'Draft',
        documentInvoiceId: null,
        documentPaymentId: null,
        documentReceiptId: null,
      };
    },
  },
};
</script>

<style scoped>
.c-avatar {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-weight: bold;
}
</style>
